import { useCallback, useMemo } from "react";

import { usePermissions } from "@refinedev/core";

export interface UseFeaturesV2Result {
  /** The practitioner's effective `scope:Permission` strings; empty until loaded */
  features: string[];
  /** True when the practitioner's featuresv2 contains exactly this `scope:Permission` key */
  hasFeature: (key: string) => boolean;
  /** True while the permissions query is still loading */
  isLoading: boolean;
  /** True when the permissions query failed; `features` is empty in that case */
  isError: boolean;
}

/**
 * The logged-in practitioner's featuresv2 permissions, as cached by the auth provider.
 *
 * Wraps Refine's `usePermissions`, which calls `authProvider.getPermissions` and caches
 * the result in react-query, so mounting this hook in many components costs one fetch.
 */
export const useFeaturesV2 = (): UseFeaturesV2Result => {
  const { data, isLoading, isError } = usePermissions<string[]>({});

  const features = useMemo(() => data ?? [], [data]);
  const hasFeature = useCallback((key: string) => features.includes(key), [features]);

  return { features, hasFeature, isLoading, isError };
};
